import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useMasters } from '../../hooks/useMasters';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { Drawer, Field, NumberInput, Select } from '../../components/ui/Form';
import { RateMaster } from '../../components/RateMaster';

interface Rate {
  id: string;
  metal_id: string;
  purity_id: string | null;
  rate_type: string;
  rate_per_gram: string | null;
  rate_per_fine_gram: string | null;
  effective_from: string;
  status: string;
}

export function RatesPage() {
  const { can } = usePermission();
  const selfApproves = can('rate.self_approve');
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const masters = useMasters();
  const [open, setOpen] = useState(false);
  // The board is the way rates are set — the same one the shop sees every
  // morning. The single-rate form stays for the odd case the board cannot
  // express: a rate effective from a particular moment, or a back-dated one.
  const [board, setBoard] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState({
    metalId: '',
    purityId: '',
    rateType: 'SALE',
    ratePerGram: '',
    ratePerFineGram: '',
    effectiveFrom: new Date().toISOString().slice(0, 16),
  });

  const { data, isLoading } = useQuery({ queryKey: ['rates'], queryFn: () => endpoints.pricing.rates() });

  const create = useMutation({
    mutationFn: () =>
      api.post('/rates', {
        metalId: form.metalId,
        ...(form.purityId ? { purityId: form.purityId } : {}),
        rateType: form.rateType,
        ...(form.ratePerGram ? { ratePerGram: form.ratePerGram } : {}),
        ...(form.ratePerFineGram ? { ratePerFineGram: form.ratePerFineGram } : {}),
        effectiveFrom: new Date(form.effectiveFrom).toISOString(),
      }),
    onSuccess: () => {
      setOpen(false);
      setForm({ ...form, ratePerGram: '', ratePerFineGram: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['rates'] });
    },
    onError: setError,
  });

  const approve = useMutation({
    mutationFn: (id: string) => api.post(`/rates/${id}/approve`),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['rates'] }),
    onError: setError,
  });

  const rows = (data?.data ?? []) as unknown as Rate[];
  const nameOf = (id: string | null, list: { id: string; code: string }[]): string =>
    list.find((x) => x.id === id)?.code ?? '—';

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <>
      <PageHeader
        title="Metal rates"
        subtitle="A published rate is never edited. A new rate supersedes the old one, and every invoice keeps the rate it was priced at."
        actions={
          can('rate.create') ? (
            <>
              <button className="btn-ghost" onClick={() => setOpen(true)}>
                One rate, own date
              </button>
              <button className="btn-primary" onClick={() => setBoard(true)}>
                Set today's rates
              </button>
            </>
          ) : null
        }
      />
      <ErrorNotice error={error} />
      {isLoading ? <Loading /> : null}

      {!isLoading && rows.length === 0 ? (
        <Empty
          message="No rates published yet."
          hint={
            selfApproves
              ? "Nothing can be priced until today's rate is entered and approved."
              : "Nothing can be priced until today's rate is entered and approved by a second person."
          }
          action={can('rate.create') ? { label: "Set today's rates", onClick: () => setBoard(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Effective from', 'Metal', 'Purity', 'Type', 'Per gram', 'Per fine gram', 'Status', '']}>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="td">{date(r.effective_from)}</td>
              <td className="td">{nameOf(r.metal_id, masters.metals)}</td>
              <td className="td">{nameOf(r.purity_id, masters.purities)}</td>
              <td className="td">{r.rate_type}</td>
              <td className="td num text-right">{r.rate_per_gram ?? '—'}</td>
              <td className="td num text-right">{r.rate_per_fine_gram ?? '—'}</td>
              <td className="td">
                {r.status === 'APPROVED' ? (
                  <span className="text-success">Approved</span>
                ) : (
                  <span className="text-danger">Awaiting approval</span>
                )}
              </td>
              <td className="td">
                {r.status !== 'APPROVED' && can('rate.approve') ? (
                  <button
                    className="btn-ghost py-1"
                    onClick={() => approve.mutate(r.id)}
                    disabled={approve.isPending}
                  >
                    Approve
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      <p className="mt-3 text-xs text-ink-soft">
        {selfApproves
          ? 'A rate prices nothing until it is approved. You can approve your own.'
          : 'A rate must be approved by someone other than the person who entered it. Until then nothing prices against it.'}
      </p>

      <RateMaster open={board} manual onClose={() => setBoard(false)} />

      {open ? (
        <Drawer
          title="Enter today's rate"
          subtitle={
            selfApproves
              ? 'This is the number every price in the shop follows from. Enter it, then approve it to publish.'
              : 'This is the number every price in the shop follows from, so it needs a second person to approve it.'
          }
          onClose={() => setOpen(false)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <Field label="Metal" required>
              <Select
                value={form.metalId}
                onChange={(v) => setForm({ ...form, metalId: v, purityId: '' })}
                options={masters.metalOptions}
              />
            </Field>

            <Field label="Purity" hint="Leave blank for a rate that applies to every purity of this metal.">
              <Select
                value={form.purityId}
                onChange={(v) => setForm({ ...form, purityId: v })}
                options={masters.puritiesFor(form.metalId).map((p) => ({ value: p.id, label: p.code }))}
                placeholder={form.metalId ? 'All purities' : 'Choose a metal first'}
              />
            </Field>

            <Field label="Rate type" required>
              <Select
                value={form.rateType}
                onChange={(v) => setForm({ ...form, rateType: v })}
                options={[
                  { value: 'SALE', label: 'Selling rate' },
                  { value: 'PURCHASE', label: 'Purchase rate (old gold)' },
                  { value: 'VALUATION', label: 'Valuation rate' },
                ]}
              />
            </Field>

            <Field label="Rate per gram" hint="Rupees per gram at the purity chosen above. Used to price jewellery.">
              <NumberInput
                value={form.ratePerGram}
                onChange={(v) => setForm({ ...form, ratePerGram: v })}
                placeholder="6850.0000"
                suffix="/g"
              />
            </Field>

            <Field
              label="Rate per fine gram"
              hint="Rupees per gram of pure metal. Old gold is valued on this, so a purchase rate needs it."
            >
              <NumberInput
                value={form.ratePerFineGram}
                onChange={(v) => setForm({ ...form, ratePerFineGram: v })}
                placeholder="7478.0000"
                suffix="/fine g"
              />
            </Field>

            {form.rateType === 'PURCHASE' && !form.ratePerFineGram ? (
              <p className="rounded border border-gold-400 bg-gold-50 p-2 text-xs">
                A purchase rate without a rate per fine gram cannot value old gold — the customer's metal is paid for
                by its pure content, not its gross weight.
              </p>
            ) : null}

            <Field label="Effective from" required hint="Documents dated before this keep the rate that applied then.">
              <input
                type="datetime-local"
                className="field"
                value={form.effectiveFrom}
                onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })}
              />
            </Field>

            <div className="flex gap-2 pt-2">
              <button
                className="btn-primary"
                disabled={!form.metalId || (!form.ratePerGram && !form.ratePerFineGram) || create.isPending}
              >
                {create.isPending ? 'Saving…' : 'Save rate'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Drawer>
      ) : null}
    </>
  );
}
